import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../api/axiosConfig';
import { coordinates } from '../utils/territory';

const LocationContext = createContext({ status: 'idle', point: null, sentAt: null, refresh: () => {} });
export const useLocationStatus = () => useContext(LocationContext);

export function LocationProvider({ children }) {
  const { user, token } = useAuth();
  const userId = user?._id || user?.id;
  const [state, setState] = useState({ status: 'idle', point: null, sentAt: null });
  const run = useRef(() => {});
  useEffect(() => {
    let alive = true, busy = false, denied = false, permission;
    setState({ status: 'idle', point: null, sentAt: null });
    if (!userId) return undefined;
    const request = (manual = false) => {
      if (!alive || busy || (denied && !manual)) return;
      if (!navigator.geolocation) { setState(previous => ({ ...previous, status: 'unsupported' })); return; }
      busy = true;
      setState(previous => ({ ...previous, status: 'requesting' }));
      navigator.geolocation.getCurrentPosition(async position => {
        if (!alive) return;
        const point = { ...coordinates(position.coords), accuracy: position.coords.accuracy, timestamp: new Date(position.timestamp || Date.now()).toISOString() };
        if (!coordinates(point)) { busy = false; setState(previous => ({ ...previous, status: 'unavailable' })); return; }
        setState(previous => ({ ...previous, status: 'sending', point }));
        try {
          const response = await api.post('location', point);
          if (alive) setState(previous => ({ ...previous, status: response.data?.ignored ? 'rejected' : 'sent', sentAt: response.data?.ignored ? previous.sentAt : Date.now() }));
        } catch {
          if (alive) setState(previous => ({ ...previous, status: 'sendError' }));
        } finally { busy = false; }
      }, error => {
        busy = false;
        if (!alive) return;
        denied = error.code === 1;
        setState(previous => ({ ...previous, status: error.code === 1 ? 'denied' : error.code === 3 ? 'timeout' : 'unavailable' }));
      }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
    };
    run.current = () => { denied = false; request(true); };
    const onPermission = () => { denied = permission.state === 'denied'; if (permission.state === 'granted') request(); };
    navigator.permissions?.query({ name: 'geolocation' }).then(value => {
      if (!alive) return;
      permission = value;
      permission.addEventListener('change', onPermission);
    }).catch(() => {});
    request(); // Preserve the existing signed-in location sender, including its 30-second interval.
    const timer = setInterval(request, 30000);
    return () => { alive = false; clearInterval(timer); permission?.removeEventListener('change', onPermission); run.current = () => {}; };
  }, [userId, token]);
  const refresh = useCallback(() => run.current(), []);
  return <LocationContext.Provider value={{ ...state, refresh }}>{children}</LocationContext.Provider>;
}
